// client/src/pages/Home.js

import {
    Box, Flex, IconButton, Tooltip, useDisclosure,
    AlertDialog, AlertDialogBody, AlertDialogFooter,
    AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Button,
    Spinner, Text, useBreakpointValue,
} from "@chakra-ui/react";
import { HamburgerIcon, AddIcon, CloseIcon } from "@chakra-ui/icons";  // Add CloseIcon import
import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import OnboardingDialog from '../components/OnboardingDialog';
import { useAuth } from '../context/AuthContext';
import { useCreditUsageContext } from '../context/CreditContext';
import { pruneOldSessions } from "../store/chatSessionStore";

// Components
import Navbar from "../components/Navbar";
import ChatInput from "../components/ChatInputContainer";
import CreditUsageAlert from "../components/CreditUsageAlert";
import HeroSection from "../components/HeroSection";
import Sidebar from "../components/Sidebar";
import LeftPane from "../pages/LeftPane";
import RightPane from "../pages/RightPane";

// Hooks & Utils
import { useChat } from "../hooks/useChat";
import { createResizeHandler } from "../utils/chatHelpers";

export default function Home() {

    const { user, checkAuth } = useAuth();
    const credit = useCreditUsageContext();
    const location = useLocation();
    const navigate = useNavigate();
    const { chatId } = useParams();
    const [showOnboarding, setShowOnboarding] = useState(false);

    const {
        isOpen: isNewChatConfirmOpen,
        onOpen: onNewChatConfirmOpen,
        onClose: onNewChatConfirmClose
    } = useDisclosure();
    const cancelNewChatRef = useRef();
    // Ref bridge to Sidebar's existing rename/delete dialogs (mobile navbar menu)
    const chatActionsRef = useRef(null);
    const isMobile = useBreakpointValue({ base: true, md: false });
    const homeScrollRef = useRef(null);

    const handleInputFocus = () => {
        if (isMobile && homeScrollRef.current) {
            setTimeout(() => {
                homeScrollRef.current?.scrollTo({
                    top: homeScrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }, 250);
        }
    };

    // Detect ?onboarding=true on page load (signup redirect)
    useEffect(() => {
        const search = location.search || window.location.search;
        const params = new URLSearchParams(search);
        if (params.get('onboarding') === 'true') {
            setShowOnboarding(true);
        }
    }, [location.search]);

    // Enforce onboarding if user's profile is incomplete
    useEffect(() => {
        if (user && user.onboardingCompleted === false) {
            setShowOnboarding(true);
        }
    }, [user]);

    useEffect(() => {
        pruneOldSessions(chatId ? [chatId] : []);
    }, [chatId]);

    const handleOnboardingComplete = async () => {
        await checkAuth(); // refresh user in AuthContext with new profile data
        setShowOnboarding(false);
        navigate('/', { replace: true }); // remove ?onboarding=true from URL
    };

    // UI State — persist sidebar across navigations
    const [isSidebarOpen, setSidebarOpen] = useState(() => {
        return sessionStorage.getItem('sidebarOpen') === 'true';
    });

    // Incremented when a new project is created — triggers sidebar to re-fetch project list
    const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);

    // Sync sidebar state to sessionStorage so it survives page navigation
    useEffect(() => {
        sessionStorage.setItem('sidebarOpen', isSidebarOpen);
    }, [isSidebarOpen]);

    // Resizable pane state
    const DEFAULT_LEFT_WIDTH = 600;
    const MIN_LEFT_WIDTH = 420;
    const MAX_LEFT_WIDTH = 900;
    const [leftWidth, setLeftWidth] = useState(DEFAULT_LEFT_WIDTH);

    // Chat logic from custom hook
    const {
        chatStarted,
        isSending,
        turns,
        activeSuggestionTurnId,
        suggestions,
        typingTurnId,
        typingSuggestionId,
        activeTurn,
        isRightPaneOpen,
        messagesContainerRef,
        rightPaneRef,
        handleSend,
        toggleSuggestion,
        toggleReport,
        onScrollMessages,
        handleRightPaneScroll,
        closeSuggestionPane,
        extractedSuggestions,
        selectedSuggestions,
        confirmSuggestionSelection,
        handleGenerateReport,
        getRightPaneContent,
        reports,
        activeReportId,
        typingReportId,
        isLoadingSuggestions,
        isLoadingReport,
        saveFeedback,
        reportLoadingPhase,
        projectId,
        projectName,
        hasMoreTurns,
        isLoadingOlderTurns,
        resetChat,
        loadOlderTurns,
        feedbackState,
        setFeedbackState,
        onEnsureExtractedSuggestions,
        suggestionExtractionStatus,
        isChatHydrating,
        stopGeneration,
        isStopping,
    } = useChat(chatId);

    const isChatRoute = Boolean(chatId);
    const isChatLoading = isChatRoute && isChatHydrating;

    const handleNewChat = () => {
        if (isSending) {
            // streaming in progress — warn user
            onNewChatConfirmOpen();
        } else {
            // safe — reset state and navigate to /
            resetChat();
            navigate('/');
        }
    };

    // Auto-navigate when a project is first created (first message sent from /)
    useEffect(() => {
        if (projectId && !chatId) {
            navigate(`/chat/${projectId}`, { replace: true });
            setSidebarRefreshKey(prev => prev + 1); // trigger sidebar to re-fetch project list
        }
    }, [projectId, chatId, navigate]);

    // Resize handler
    const handleSeparatorMouseDown = createResizeHandler(
        leftWidth,
        setLeftWidth,
        MIN_LEFT_WIDTH,
        MAX_LEFT_WIDTH
    );

    const rightPaneContent = getRightPaneContent();
    const isTypingInRightPane =
        (typingSuggestionId === activeSuggestionTurnId && rightPaneContent?.type === 'suggestions') ||
        (typingReportId?.turnId === activeSuggestionTurnId && typingReportId?.version === activeReportId?.version);

    return (
        <Box>
            <Navbar
                isHomeView={!chatId && !chatStarted}
                showNewChat={chatStarted}
                showChatActions={Boolean(chatId && projectId)}
                onToggleSidebar={() => setSidebarOpen(prev => !prev)}
                onNewChat={handleNewChat}
                onRenameActiveChat={() => chatActionsRef.current?.openRename?.({ id: projectId, name: projectName })}
                onDeleteActiveChat={() => chatActionsRef.current?.openDelete?.({ id: projectId, name: projectName })}
            />
            <Box
                ml={{ base: 0, md: 2 }}
                // minHeight="100vh"
            >
                {/* Desktop Sidebar toggle + New Chat — vertical stack, moves with sidebar */}
                <Flex
                    display={{ base: "none", md: "flex" }}
                    position="fixed"
                    mt={4}
                    ml={1}
                    left={isSidebarOpen ? "270px" : "20px"}
                    transition="left 0.25s ease"
                    zIndex={1200}
                    flexDirection="column"
                    gap="8px"
                >
                    {/* Hamburger — desktop toggle */}
                    <IconButton
                        icon={isSidebarOpen ? <CloseIcon boxSize={3} color="#CF008A" /> : <HamburgerIcon />}
                        aria-label={isSidebarOpen ? "Close Sidebar" : "Toggle Sidebar"}
                        bg="white"
                        border={isSidebarOpen ? "2px solid #CF008A" : "1px solid #ECECEC"}
                        color="#242424"
                        borderRadius="12px"
                        _hover={{ bg: "#fff", color: "#CF008A", border: "2px solid #CF008A" }}
                        onClick={() => setSidebarOpen(prev => !prev)}
                    />

                    {/* New Chat — only in split view, fades when sidebar open */}
                    {chatStarted && (
                        <Tooltip
                            label="New Chat"
                            placement="right"
                            bg="#CF008A"
                            color="white"
                            fontSize="sm"
                            fontWeight="medium"
                            px={3}
                            py={2}
                            borderRadius="8px"
                            hasArrow
                            arrowSize={8}
                        >
                            <IconButton
                                icon={<AddIcon />}
                                aria-label="New Chat"
                                bg="white"
                                border="2px solid #CF008A"
                                color="#CF008A"
                                borderRadius="12px"
                                opacity={isSidebarOpen ? 0 : 1}
                                pointerEvents={isSidebarOpen ? 'none' : 'auto'}
                                transition="opacity 0.2s ease"
                                _hover={{ bg: "#CF008A", color: "white" }}
                                onClick={handleNewChat}
                            />
                        </Tooltip>
                    )}
                </Flex>

                {/* Mobile close button when sidebar is open */}
                {isSidebarOpen && (
                    <IconButton
                        icon={<CloseIcon boxSize={3} color="#CF008A" />}
                        aria-label="Close Sidebar"
                        display={{ base: "inline-flex", md: "none" }}
                        position="fixed"
                        top="10px"
                        right="16px"
                        zIndex={1350}
                        bg="white"
                        border="2px solid #CF008A"
                        color="#242424"
                        borderRadius="12px"
                        _hover={{ bg: "#fff", color: "#CF008A", border: "2px solid #CF008A" }}
                        onClick={() => setSidebarOpen(false)}
                    />
                )}

                <Sidebar
                    isOpen={isSidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    activeProjectId={projectId}
                    refreshKey={sidebarRefreshKey}
                    dialogApiRef={chatActionsRef}
                    onNewProject={() => { resetChat(); navigate('/'); }}
                    onSelectProject={(id, name) => {
                        navigate(`/chat/${id}`);
                    }}
                />

                {/* Main Content Area */}
                <>
                    {!chatId && !chatStarted ? (
                        // HOME VIEW
                        <Box
                            ref={homeScrollRef}
                            maxHeight={{ base: "calc(100dvh - 60px)", md: "none" }}
                            height={{ base: "calc(100dvh - 60px)", md: "auto" }}
                            minHeight={{ base: "calc(100dvh - 60px)", md: "100vh" }}
                            overflowY={{ base: "auto", md: "visible" }}
                            boxSizing='border-box'
                            transition="margin-left 0.25s ease"
                            marginLeft={{ base: "0px", md: isSidebarOpen ? "250px" : "0px" }}

                            border={{ base: "none", md: "1px solid #ECECEC" }}
                            borderRadius={{ base: 0, md: "20px" }}
                            mt={{ base: "60px", md: 75 }}
                            mr={{ base: 0, md: 2 }}
                            mb={{ base: 0, md: 2 }}
                            bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
                        >
                            <Flex
                                justify="center"
                                h={{ base: "100%", md: "auto" }}
                                px={{ base: 2, md: 4 }}
                                py={{ base: 2, md: 0 }}
                            >
                                <Flex
                                    direction="column"
                                    justify={{ base: "space-between", md: "flex-start" }}
                                    h={{ base: "100%", md: "auto" }}
                                    maxW={{ base: "100%", md: "clamp(800px, 50vw, 2000px)" }}
                                    width="100%"
                                >
                                    <Box
                                        flex={{ base: 1, md: "none" }}
                                        display={{ base: "flex", md: "block" }}
                                        flexDirection="column"
                                        justifyContent={{ base: "center", md: "flex-start" }}
                                        alignItems="center"
                                        w="100%"
                                    >
                                        <HeroSection />
                                    </Box>
                                    <Box
                                        mt={{ base: "auto", md: 0 }}
                                        mb={{ base: 2, md: 0 }}
                                        w="100%"
                                    >
                                        <CreditUsageAlert />
                                        <ChatInput
                                            key={chatId || 'draft'}
                                            chatId={chatId}
                                            onSend={handleSend}
                                            onInputFocus={isMobile ? handleInputFocus : undefined}
                                            mode="hero"
                                            isLoading={isSending}
                                            onStop={stopGeneration}
                                            isStopping={isStopping}
                                            disabled={credit.isCreditUnavailable}
                                        />
                                    </Box>
                                </Flex>
                            </Flex>
                        </Box>
                    ) : (
                        <Box
                            maxHeight={{ base: "calc(100dvh - 60px)", md: "100vh" }}
                            height={{ base: "calc(100dvh - 60px)", md: "auto" }}
                            boxSizing='border-box'
                            transition="margin-left 0.25s ease"
                            marginLeft={{ base: "0px", md: isSidebarOpen ? "250px" : "0px" }}

                            border={{ base: "none", md: "1px solid #ECECEC" }}
                            borderRadius={{ base: 0, md: "20px" }}
                            mt={{ base: "60px", md: 75 }}
                            mr={{ base: 0, md: 2 }}
                            mb={{ base: 0, md: 2 }}
                            bgGradient="linear(to-b, #EAFFFF 0%, white 30%, white 60%, #EAFFFF 100%)"
                        >
                            <Flex
                                h={{ base: "100%", md: "calc(100vh - 90px)" }}
                                px={{ base: 0, md: 2 }}
                                py={{ base: 0, md: 2 }}
                                overflow="hidden"
                            >
                                {/* LEFT PANE */}
                                {isChatLoading ? (
                                    <Flex
                                        flex={{ base: "1", md: isRightPaneOpen ? "0 0 auto" : "1" }}
                                        w={{ base: "100%", md: isRightPaneOpen ? `${leftWidth}px` : "100%" }}
                                        h="100%"
                                        align="center"
                                        justify="center"
                                    >
                                        <Flex align="center" gap={3} direction="row" bg="white" p={6} borderRadius="16px" boxShadow="sm">
                                            <Spinner size="lg" color="#CF008A" />
                                            <Text fontSize="md" color="#4A5568">Loading chat…</Text>
                                        </Flex>
                                    </Flex>
                                ) : (
                                    <Box
                                        flex={{ base: "1", md: isRightPaneOpen ? "0 0 auto" : "1" }}
                                        w={{ base: "100%", md: isRightPaneOpen ? `${leftWidth}px` : "100%" }}
                                        h="100%"
                                        overflow="hidden"
                                        display={{ base: isRightPaneOpen ? "none" : "block", md: "block" }}
                                    >
                                        <LeftPane
                                            key={chatId || 'draft'}
                                            chatId={chatId}
                                            turns={turns}
                                            activeSuggestionTurnId={activeSuggestionTurnId}
                                            onSendMessage={handleSend}
                                            isLoading={isSending}
                                            messagesContainerRef={messagesContainerRef}
                                            onScrollMessages={onScrollMessages}
                                            isRightPaneOpen={isRightPaneOpen}
                                            typingTurnId={typingTurnId}
                                            typingSuggestionId={typingSuggestionId}
                                            onToggleSuggestion={toggleSuggestion}
                                            reports={reports}
                                            activeReportId={activeReportId}
                                            onToggleReport={toggleReport}
                                            typingReportId={typingReportId}
                                            isLoadingReport={isLoadingReport}
                                            isLoadingSuggestions={isLoadingSuggestions}
                                            onSubmitFeedback={saveFeedback}
                                            hasMoreTurns={hasMoreTurns}
                                            isLoadingOlderTurns={isLoadingOlderTurns}
                                            onLoadOlderTurns={loadOlderTurns}
                                            feedbackState={feedbackState}
                                            setFeedbackState={setFeedbackState}
                                            onStopGeneration={stopGeneration}
                                            isStopping={isStopping}
                                        />
                                    </Box>
                                )}

                                {/* DIVIDER */}
                                {isRightPaneOpen && (
                                    <Box
                                        display={{ base: "none", md: "block" }}
                                        w="8px"
                                        bg="transparent"
                                        cursor="col-resize"
                                        _hover={{ bg: "transparent" }}
                                        onMouseDown={handleSeparatorMouseDown}
                                    />
                                )}

                                {/* RIGHT PANE */}
                                {isRightPaneOpen && (
                                    <Box flex="1" h="100%" overflow="hidden">
                                    <Box
                                        flex="1"
                                        w={{ base: "100%", md: "auto" }}
                                        h="100%"
                                        overflow="hidden"
                                        display={{ base: isRightPaneOpen ? "block" : "none", md: "block" }}
                                    >
                                        <RightPane
                                            activeTurn={activeTurn}
                                            onClose={closeSuggestionPane}
                                            onScroll={handleRightPaneScroll}
                                            typingSuggestionId={typingSuggestionId}
                                            feedbackState={feedbackState}
                                            setFeedbackState={setFeedbackState}
                                            rightPaneRef={rightPaneRef}
                                            content={rightPaneContent}
                                            typingIndicatorVisible={isTypingInRightPane}
                                            extractedSuggestions={extractedSuggestions[activeSuggestionTurnId]}
                                            selectedSuggestions={selectedSuggestions[activeSuggestionTurnId]}
                                            onConfirmSelection={confirmSuggestionSelection}
                                            onGenerateReport={handleGenerateReport}
                                            turnId={activeSuggestionTurnId}
                                            typingReportId={typingReportId}
                                            isLoadingReport={isLoadingReport}
                                            onSubmitFeedback={saveFeedback}
                                            reportLoadingPhase={reportLoadingPhase}
                                            onEnsureExtractedSuggestions={onEnsureExtractedSuggestions}
                                            suggestionExtractionStatus={suggestionExtractionStatus[activeSuggestionTurnId]}
                                            isMobile={isMobile}
                                        />
                                    </Box>
                                    </Box>
                                )}
                            </Flex>
                        </Box>
                    )}
                </>

                {/* Bounce animation for typing indicators */}
                <style>
                    {`
                    @keyframes bounce {
                        0%, 100% {
                        transform: translateY(0);
                        }
                        50% {
                        transform: translateY(-5px);
                        }
                    }
                    `}
                </style>
            </Box>

            <OnboardingDialog
                isOpen={showOnboarding}
                user={user}
                onComplete={handleOnboardingComplete}
            />
            <AlertDialog
                isOpen={isNewChatConfirmOpen}
                leastDestructiveRef={cancelNewChatRef}
                onClose={onNewChatConfirmClose}
                isCentered
            >
                <AlertDialogOverlay backdropFilter="blur(4px)" />
                <AlertDialogContent borderRadius="16px" p={2} mx={{ base: 3, md: "auto" }}>
                    <AlertDialogHeader fontSize="18px" fontWeight="bold" color="#1A1A1A">
                        Response in progress
                    </AlertDialogHeader>
                    <AlertDialogBody fontSize="14px" color="#6D6D6D">
                        The current response hasn't finished yet and may not be saved.
                        Are you sure you want to start a new chat?
                    </AlertDialogBody>
                    <AlertDialogFooter gap="8px">
                        <Button
                            ref={cancelNewChatRef}
                            variant="ghost"
                            color="#6D6D6D"
                            borderRadius="10px"
                            _hover={{ bg: '#F5F5F5' }}
                            onClick={onNewChatConfirmClose}
                        >
                            Wait
                        </Button>
                        <Button
                            bg="#CF008A"
                            color="white"
                            borderRadius="10px"
                            _hover={{ bg: '#D933A1' }}
                            onClick={() => { onNewChatConfirmClose(); resetChat(); navigate('/'); }}
                        >
                            Start new chat
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Box>
    );
}